import { useCallback } from "react";

import {
  dashboardFederationsControllerAvailableByPublicId,
  dashboardFederationsControllerAvailableSearch,
} from "@/api/__generated__/dashboard-federations/dashboard-federations";
import { OrganizationLogo } from "@/components/organization/OrganizationLogo";
import { useCurrentPlatform } from "@/contexts";
import { useT } from "@/i18n";
import { mapToAvailableFederation, mapToOrganizationReference } from "@/mappers";
import { OrganizationReference } from "@/types";
import { AsyncInlineCommand, AsyncInlineCommandProps } from "@/ui";

const SEARCH_LIMIT = 25;
const MIN_KEYWORD_LENGTH = 2;

export type AvailableOrganizationAsyncInlineCommandProps = Omit<
  AsyncInlineCommandProps<OrganizationReference, number>,
  "loadOptions" | "getOptionValue" | "getOptionLabel" | "getOptionIcon" | "label" | "placeholder"
> & {
  label?: string;
  placeholder?: string;
  excludeFederationIds?: string[];
  /**
   * Organization ids pinned to the top of the default (un-searched) list, in the
   * given order. Deduplicated against each other, `excludeFederationIds`, and the
   * paginated results; resolved through the `available` by-id endpoint so an id
   * outside the caller's available set is silently dropped rather than rendered
   * as an unswitchable option. Keyword search results keep server order — pinning
   * affects the default ordering only, pinned entries stay filterable.
   */
  pinnedFederationIds?: string[];
};

export const AvailableOrganizationAsyncInlineCommand = ({
  label,
  placeholder,
  excludeFederationIds,
  pinnedFederationIds,
  ...props
}: AvailableOrganizationAsyncInlineCommandProps) => {
  const t = useT();
  const currentPlatform = useCurrentPlatform();

  const loadPinnedFederations = useCallback(
    async (ids: string[], signal?: AbortSignal): Promise<OrganizationReference[]> => {
      const results = await Promise.all(
        ids.map(async (id) => {
          try {
            const federation = await dashboardFederationsControllerAvailableByPublicId(
              id,
              { platformId: currentPlatform.id },
              undefined,
              signal
            );
            return mapToAvailableFederation(federation);
          } catch {
            // 404 = outside the caller's available set (stale owner/dataOwner or no
            // access) — drop the pin instead of surfacing an unswitchable option.
            return null;
          }
        })
      );
      return results.filter((federation) => federation !== null);
    },
    [currentPlatform.id]
  );

  const loadFederations = useCallback(
    async (query: string, options?: { cursor?: number; signal?: AbortSignal }) => {
      const page = options?.cursor ?? 1;
      const trimmed = query.trim();
      const keyword = trimmed.length >= MIN_KEYWORD_LENGTH ? trimmed : undefined;

      // Pinning applies to the default listing only — with a keyword the server
      // ordering stands and pinned organizations match like any other result.
      const pinnedIds = keyword
        ? []
        : (pinnedFederationIds ?? []).filter(
            (id, index, ids) => ids.indexOf(id) === index && !excludeFederationIds?.includes(id)
          );

      // `/available/search` always returns slim refs — `include` is not declared on the
      // available input class, so it cannot be set here at the type level.
      const [result, pinnedItems] = await Promise.all([
        dashboardFederationsControllerAvailableSearch(
          {
            ...(keyword && { keyword }),
            page,
            limit: SEARCH_LIMIT,
            platformIds: [currentPlatform.id],
          },
          undefined,
          options?.signal
        ),
        page === 1 && pinnedIds.length > 0
          ? loadPinnedFederations(pinnedIds, options?.signal)
          : Promise.resolve<OrganizationReference[]>([]),
      ]);

      const items = result.data
        .map(mapToOrganizationReference)
        .filter(
          (federation) =>
            !excludeFederationIds?.includes(federation.id) && !pinnedIds.includes(federation.id)
        );
      const hasMore = page < result.meta.totalPages;

      return { items: [...pinnedItems, ...items], cursor: hasMore ? page + 1 : null };
    },
    [currentPlatform.id, excludeFederationIds, pinnedFederationIds, loadPinnedFederations]
  );

  return (
    <AsyncInlineCommand<OrganizationReference, number>
      loadOptions={loadFederations}
      loadOnMount
      getOptionValue={(federation) => String(federation.id)}
      getOptionLabel={(federation) => federation.name}
      getOptionIcon={(federation, { width, height }) => (
        <OrganizationLogo organization={federation} width={width} height={height} />
      )}
      label={label ?? t("organization.components.globalSelect.label")}
      placeholder={placeholder ?? t("organization.components.globalSelect.placeholder")}
      {...props}
    />
  );
};
